import {
  Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-selectorini',
  standalone: false,
  templateUrl: './selectorini.html',
  styleUrl: './selectorini.css'
})
export class Selectorini {
  @Input() options: any[] = [];
  @Input() displayKey: string = 'label';

  /** when true, store/emit option[valueKey] (primitive); when false, store the whole option object */
  @Input() bindValue: boolean = true;
  @Input() valueKey: string = 'value';

  /** single primitive/object or array (when multiple=true) */
  @Input() value: any | any[] = null;
  @Output() valueChange = new EventEmitter<any | any[]>();

  @Input() placeholder = 'Select...';
  @Input() multiple = false;
  @Input() disabled = false;
  @Input() searchable = true;
  @Input() icon?: string;

  isOpen = false;
  dropUp = false;

  searchBuffer = '';
  private bufferTimeout: any;

  @ViewChild('menuEl') menuEl?: { nativeElement: HTMLElement };

  constructor(private eRef: ElementRef<HTMLElement>) {}

  ngOnChanges() {
    // normalize shape based on 'multiple'
    if (this.multiple) {
      this.value = Array.isArray(this.value) ? [...this.value] : (this.value != null ? [this.value] : []);
    } else if (Array.isArray(this.value)) {
      this.value = this.value[0] ?? null;
    }
  }

  /** stable key for equality checks */
  private keyOf = (x: any) => (x == null ? x : (typeof x === 'object' ? (x.id ?? x[this.valueKey] ?? x) : x));

  /** find the option object for a stored item (primitive or object) */
  private findOption(item: any): any | undefined {
    if (item == null) return undefined;
    if (typeof item === 'object') return item;
    return this.options.find(o => (o?.[this.valueKey] ?? o) === item);
  }

  /** label for any stored value/option */
  getLabel(item: any): string {
    const opt = this.findOption(item);
    if (!opt) return String(item ?? '');
    if (this.displayKey in opt) return String(opt[this.displayKey]);
    if ('label' in opt) return String(opt.label);
    if ('name' in opt) return String(opt.name);
    if ('title' in opt) return String(opt.title);
    return String(item ?? '');
  }

  isSelected(option: any): boolean {
    const candidate = this.bindValue ? (option?.[this.valueKey] ?? option) : option;
    if (this.multiple) {
      const arr = (this.value as any[]) ?? [];
      return arr.some(v => this.keyOf(v) === this.keyOf(candidate));
    }
    return this.keyOf(this.value) === this.keyOf(candidate);
  }

  toggleDropdown() {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.searchBuffer = '';
    if (this.isOpen) setTimeout(() => this.computeDropDirection(), 0);
  }

  selectItem(option: any) {
    const payload = this.bindValue ? (option?.[this.valueKey] ?? option) : option;

    if (this.multiple) {
      const arr = (this.value as any[]) ?? [];
      const exists = arr.some(v => this.keyOf(v) === this.keyOf(payload));
      this.value = exists ? arr.filter(v => this.keyOf(v) !== this.keyOf(payload)) : [...arr, payload];
      this.valueChange.emit(this.value);
    } else {
      this.value = payload;
      this.valueChange.emit(this.value);
      this.isOpen = false;
    }
  }

  removeItem(item: any) {
    if (!this.multiple) return;
    const arr = (this.value as any[]) ?? [];
    this.value = arr.filter(v => this.keyOf(v) !== this.keyOf(item));
    this.valueChange.emit(this.value);
  }

  get filteredOptions(): any[] {
    if (!this.searchable || !this.searchBuffer.trim()) return this.options;
    const norm = (v: string) => (v || '').toLowerCase().replace(/^\+/, '').replace(/\s+/g, '');
    const s = norm(this.searchBuffer);
    return this.options.filter(o => norm(this.getLabel(o)).includes(s));
  }

  @HostListener('document:click', ['$event.target'])
  clickOutside(target: EventTarget | null) {
    if (target instanceof Node && !this.eRef.nativeElement.contains(target)) {
      this.isOpen = false;
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange() {
    if (this.isOpen) this.computeDropDirection();
  }

  private computeDropDirection() {
    const hostEl = this.eRef.nativeElement;
    const ctrl = hostEl.querySelector('.selectorini-control') as HTMLElement | null;
    const rect = (ctrl ?? hostEl).getBoundingClientRect();

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const spaceBelow = viewportH - rect.bottom;

    const menuEl = this.menuEl?.nativeElement;
    const menuH = menuEl ? Math.min(menuEl.scrollHeight, 280) : 240; // use max-height fallback

    this.dropUp = spaceBelow < menuH;
  }

  /** helpers for template */
  get valueArray(): any[] {
    return this.multiple ? (Array.isArray(this.value) ? this.value as any[] : []) : (this.value != null ? [this.value] : []);
  }
  get hasSelection(): boolean {
    return this.multiple ? this.valueArray.length > 0 : this.value != null && this.value !== '';
  }
}
